import React from 'react';
import { Box, Typography, Link, Avatar, Stack, Button } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import { motion } from 'framer-motion';

const links = [
  { icon: <DescriptionIcon />, label: 'CV', url: '/resume.pdf' },
  { icon: <SchoolIcon />, label: 'Scholar', url: 'https://scholar.google.com/citations?user=YlZVV_wAAAAJ&hl=en' },
  { icon: <GitHubIcon />, label: 'GitHub', url: 'https://github.com/anastasiya-masalava' },
  { icon: <LinkedInIcon />, label: 'LinkedIn', url: 'https://www.linkedin.com/in/anastasiyamasalava/' },
  { icon: <EmailIcon />, label: 'Email', url: 'mailto:masalava@stanford.edu' },
];

const ExtLink = ({ href, children }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const About = () => {
  return (
    <Box component="section" id="top" sx={{ scrollMarginTop: 80, pt: { xs: 4, md: 7 }, pb: { xs: 4, md: 5 } }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 5 }} alignItems={{ xs: 'center', sm: 'flex-start' }}>
          <Box sx={{ flexShrink: 0, width: { xs: 150, sm: 180 } }}>
            <Avatar
              src="/profile_picture.jpg"
              alt="Anastasiya Masalava"
              sx={{ width: { xs: 150, sm: 180 }, height: { xs: 150, sm: 180 } }}
            >
              AM
            </Avatar>
          </Box>

          <Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, mb: { xs: 3, sm: 2 } }}>
              <Typography component="h1" variant="h3" sx={{ mb: 0.5, fontSize: { xs: '1.9rem', sm: '2.25rem' } }}>
                Anastasiya Masalava
              </Typography>
              <Typography color="text.secondary">masalava [at] stanford [dot] edu</Typography>
            </Box>

            <Typography paragraph>
              I am a Master's student in Computer Science at{' '}
              Stanford University. My research interests sit at the
              intersection of <strong>robotics</strong> and <strong>personalization</strong>: building learning
              systems that adapt to the people they serve and that know when they can be trusted.
            </Typography>
            <Typography paragraph>
              At the <ExtLink href="https://sisl.stanford.edu/">Stanford Intelligent Systems Laboratory (SISL)</ExtLink>,
              advised by <ExtLink href="https://mykel.kochenderfer.com/">Dr. Mykel Kochenderfer</ExtLink>, I work on
              failure detection for robot world models under distribution shift. I also research recommendation systems and personalization
              in the <ExtLink href="https://www.gsb.stanford.edu/faculty-research/labs-initiatives/sil">Golub Capital Social Impact Lab</ExtLink>{' '}
              with <ExtLink href="https://sites.google.com/stanford.edu/yuyan-wang">Dr. Yuyan Wang</ExtLink>.
            </Typography>
            <Typography paragraph>
              I received my B.S. in Computer Science from{' '}
              Georgia Tech, where I worked on computer vision for
              earthquake damage assessment with{' '}
              <ExtLink href="https://www.cse.gatech.edu/people/mahdi-roozbahani">Dr. Mahdi Roozbahani</ExtLink> and{' '}
              <ExtLink href="https://ce.gatech.edu/directory/person/j-david-frost">Dr. J. David Frost</ExtLink>.
            </Typography>

            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {links.map((item) => (
                <Button
                  key={item.label}
                  href={item.url}
                  target={item.url.startsWith('http') || item.url.endsWith('.pdf') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  variant={item.label === 'CV' ? 'contained' : 'outlined'}
                  disableElevation
                  size="small"
                  startIcon={item.icon}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Box>
        </Stack>
      </motion.div>
    </Box>
  );
};

export default About;
